import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import { api } from "../../auth/api";
import { useAuth } from "../../auth/AuthContext";
import TurnstileField from "../../components/TurnstileField";
import ForSaleTab from "./ForSaleTab";
import "./Members.css";

const MEMBER_TABS = [
  { id: "management", label: "Management", section: "Club Management" },
  { id: "committee", label: "Committee" },
  { id: "calendar", label: "Calendar" },
  { id: "documents", label: "Documents", section: "Club Documents" },
  { id: "results", label: "Results", section: "Results" },
  { id: "for-sale", label: "For Sale" },
  { id: "contacts", label: "Contacts", section: "Contacts" },
];

const COMMITTEE_MEMBERS = [
  {
    name: "Mike Scarlett",
    title: "Chairman",
  },
  {
    name: "Keith Norman",
    title: "Secretary",
  },
  {
    name: "Les Lawrence",
    title: "Treasurer",
  },
  {
    name: "Paul Reeves",
    title: "Training Coordinator",
  },
  {
    name: "Iain Guy",
    title: "Safety Officer",
  },
  {
    name: "Albert Peck",
    title: "Technical Officer",
  },
];

const DOCUMENTS_EXTRAS = [
  {
    id: "novice-guide",
    title: "New Member Guide",
    description: "How novices join and progress at RSC",
    url: "/novice-guide",
    internal: true,
  },
];

function LoginForm({ onSubmitted }) {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [devLink, setDevLink] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setMessage({ type: "error", text: "Please complete the Turnstile challenge" });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      const data = await api.requestLink(email, token);
      if (data.dev_magic_link) {
        setDevLink(data.dev_magic_link);
        setMessage({
          type: "success",
          text: "Magic link ready (local dev).",
        });
      } else if (data.dev_error === "not_allowlisted") {
        setDevLink(null);
        setMessage({
          type: "error",
          text:
            data.message ||
            "That email is not an active member in the local database.",
        });
      } else {
        setDevLink(null);
        setMessage({
          type: "success",
          text:
            data.message ||
            "If that email is registered, you will receive a sign-in link shortly.",
        });
      }
      onSubmitted?.();
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Could not request link" });
    } finally {
      setSubmitting(false);
      setToken(null);
      setTurnstileKey((k) => k + 1);
    }
  };

  return (
    <div className="login-panel">
      <h1>Members sign in</h1>
      <p className="help-text">
        Enter the email address you use with Range Mate. We will email you a
        one-time sign-in link — no password required.
      </p>
      {message && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}
      {devLink ? (
        <div className="message message-info">
          <strong>Local dev magic link:</strong>
          <div style={{ marginTop: "0.5rem" }}>
            <a href={devLink}>{devLink}</a>
          </div>
        </div>
      ) : null}
      <form onSubmit={handleSubmit}>
        <div className="form-field">
          <label htmlFor="member-email">Range Mate email</label>
          <input
            id="member-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </div>
        <TurnstileField
          key={turnstileKey}
          onVerify={setToken}
          onExpire={() => setToken(null)}
          onError={() => setToken(null)}
        />
        <button className="btn" type="submit" disabled={submitting} style={{ marginTop: "1rem" }}>
          {submitting ? "Sending…" : "Email me a sign-in link"}
        </button>
      </form>
    </div>
  );
}

function CommitteeTab() {
  return (
    <section className="members-tab-panel" aria-labelledby="committee-heading">
      <h2 id="committee-heading">Committee</h2>
      <div className="committee-grid">
        {COMMITTEE_MEMBERS.map((member) => (
          <div key={member.title} className="committee-card">
            <div className="committee-card-icon" aria-hidden="true">
              👤
            </div>
            <h3>{member.name}</h3>
            <p>{member.title}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ClubCalendarTab({ src }) {
  return (
    <section className="members-tab-panel" aria-labelledby="calendar-heading">
      <h2 id="calendar-heading">Calendar</h2>
      <p className="help-text">
        Upcoming RSC members shoots from Range Mate. Open Range Mate to book.
      </p>
      {src ? (
        <div className="calendar-embed">
          <iframe
            src={src}
            width="100%"
            height="600"
            style={{ border: 0 }}
            loading="lazy"
            title="RSC Members Shoot Calendar"
          />
        </div>
      ) : (
        <p className="muted">Calendar is not available right now.</p>
      )}
    </section>
  );
}

function SectionResourcesTab({ label, section, items, loading, error }) {
  const headingId = `section-${section.replace(/\s+/g, "-").toLowerCase()}`;

  if (loading) {
    return <p>Loading…</p>;
  }

  if (error) {
    return <div className="message message-error">{error}</div>;
  }

  return (
    <section className="members-tab-panel" aria-labelledby={headingId}>
      <h2 id={headingId}>{label}</h2>
      <div className="card-grid">
        <div className="resource-card">
          <ul>
            {items.map((item) => (
              <li key={item.id}>
                {item.internal ? (
                  <Link to={item.url}>{item.title}</Link>
                ) : (
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    {item.title}
                  </a>
                )}
                {item.description ? (
                  <p className="resource-desc">{item.description}</p>
                ) : null}
                {item.title === "Practiscore Competition Results" ? (
                  <p className="resource-desc">
                    <strong>
                      <em>Please Note</em>
                    </strong>
                    : Practiscore may require you to sign up and follow the club
                    to see the results.
                  </p>
                ) : null}
              </li>
            ))}
            {!items.length ? (
              <li className="muted">No links available for this section.</li>
            ) : null}
          </ul>
        </div>
      </div>
    </section>
  );
}

function MembersArea({ member, logout }) {
  const [tab, setTab] = useState("management");
  const [resources, setResources] = useState([]);
  const [resourcesError, setResourcesError] = useState("");
  const [resourcesLoading, setResourcesLoading] = useState(true);

  useEffect(() => {
    api
      .resources()
      .then((data) => setResources(data.resources || []))
      .catch((err) => setResourcesError(err.message || "Failed to load resources"))
      .finally(() => setResourcesLoading(false));
  }, []);

  const activeTab = MEMBER_TABS.find((t) => t.id === tab) || MEMBER_TABS[0];
  const sectionItems = activeTab.section
    ? resources.filter((r) => r.section === activeTab.section)
    : [];
  const calendarSrc = (resources.find((r) => r.section === "Calendar") || {})
    .url;
  const extraItems = activeTab.id === "documents" ? DOCUMENTS_EXTRAS : [];

  return (
    <>
      <div className="members-toolbar">
        <h1 className="members-welcome">Welcome {member.first_name}</h1>
        <div className="members-actions">
          {member.is_admin ? (
            <Link className="btn" to="/members/admin">
              Admin area
              {member.pending_access_count > 0 ? (
                <span className="badge">{member.pending_access_count}</span>
              ) : null}
            </Link>
          ) : null}
          <button className="btn btn-secondary" type="button" onClick={logout}>
            Log out
          </button>
        </div>
      </div>

      <div className="members-tabs" role="tablist" aria-label="Members area">
        {MEMBER_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`members-tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`members-panel-${t.id}`}
            className={`members-tab${tab === t.id ? " active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        id={`members-panel-${tab}`}
        role="tabpanel"
        aria-labelledby={`members-tab-${tab}`}
      >
        {tab === "committee" ? (
          <CommitteeTab />
        ) : tab === "calendar" ? (
          <ClubCalendarTab src={calendarSrc} />
        ) : tab === "for-sale" ? (
          <ForSaleTab />
        ) : (
          <SectionResourcesTab
            label={activeTab.label}
            section={activeTab.section}
            items={[...sectionItems, ...extraItems]}
            loading={resourcesLoading}
            error={resourcesError}
          />
        )}
      </div>

      <div className="members-alert members-alert-footer">
        <strong>Authorized Access Only:</strong>
        <p style={{ margin: "0.5rem 0 0" }}>
          This page is restricted to registered members of Romsey Shooting
          Club. Unauthorized access is prohibited.
        </p>
      </div>
    </>
  );
}

function MembersPage() {
  const { member, loading, logout } = useAuth();

  if (loading) {
    return (
      <>
        <Header />
        <main className="members-page">
          <p>Loading…</p>
        </main>
        <Footer />
      </>
    );
  }

  if (!member) {
    return (
      <>
        <Header />
        <main className="members-page">
          <LoginForm />
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="members-page">
        <MembersArea member={member} logout={logout} />
      </main>
      <Footer />
    </>
  );
}

export default MembersPage;
